import { Controller } from "@hotwired/stimulus";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import * as XLSX from "xlsx";
import moment from "moment";
import DateRangePicker from "../vanilla-datetimerange-picker";

// Connects to data-controller="tabulator".
export default class extends Controller {
  // localStorage key used to remember this table's last filters, sorters, page, and page size.
  static storageKey = "fieldforms:gauge-readings-table-state";

  // Stimulus values supplied by app/views/gauge_readings/index.html.erb.
  static values = {
    ajaxUrl: String,
    columns: Array,
  };

  // Stimulus targets inside app/views/gauge_readings/index.html.erb.
  static targets = [
    "table",
    "photoCapturedAtRange",
    "photoCapturedAtStartDay",
    "photoCapturedAtStartMonth",
    "photoCapturedAtStartYear",
    "photoCapturedAtStartTime",
    "photoCapturedAtEndDay",
    "photoCapturedAtEndMonth",
    "photoCapturedAtEndYear",
    "photoCapturedAtEndTime",
  ];

  // Initializes saved table state and mounts Tabulator.
  connect() {
    // URL state wins over localStorage; localStorage wins over the built-in defaults.
    this.tableState = this.initialTableState();
    // Build the Tabulator instance once the initial state has been resolved.
    this.initializeTabulator();
    // Mount the external photo-captured-at range picker after Tabulator exists.
    this.initializePhotoCapturedAtRangePicker();
    // Leave the current Rails-provided AJAX URL visible in development console output.
    // console.log("AJAX URL:", this.ajaxUrlValue);
    // Leave the current Rails-provided column metadata visible in development console output.
    // console.log("Columns:", this.columnsValue);
  }

  // Tears down the Tabulator instance if it exists.
  // Called automatically by Stimulus before the controller disconnects from the DOM.
  // Returns undefined.
  disconnect() {
    // Remove scroll listeners that were attached outside Tabulator itself.
    this.unregisterScrollTracking();
    // Remove the date range picker markup and global listeners.
    this.removePhotoCapturedAtRangePicker();
    // Destroying Tabulator prevents stale event listeners when Turbo replaces the page.
    this.table?.destroy();
  }

  // Creates the Tabulator table with remote pagination, filtering, sorting, and persisted state.
  // Called by connect() after this.tableState has been initialized.
  // Returns undefined.
  initializeTabulator() {
    // Store the Tabulator instance so disconnect() can destroy it later.
    this.table = new Tabulator(this.tableTarget, {
      // Convert Tabulator's request params into Rails-compatible query params.
      ajaxURLGenerator: (url, _config, params) => {
        // Save every AJAX-driving state change before the request is sent.
        this.persistTableState(params);
        // Return the URL Tabulator should fetch for this remote data request.
        return `${url}?${this.ajaxQueryString(params)}`;
      },
      // Rails JSON endpoint that returns Tabulator rows.
      ajaxURL: this.ajaxUrlValue,
      // Column definitions built from GaugeReading::COLUMN_META.
      columns: this.tableColumns(),
      // Tell Tabulator that filtering happens on the server.
      filterMode: "remote",
      // Fill the table's parent container.
      height: "100%",
      // Restore visible header filter input values from URL/localStorage state.
      initialHeaderFilter: this.initialHeaderFilters(),
      // Restore external filters such as the photo-captured-at date range.
      initialFilter: this.initialProgrammaticFilters(),
      // Restore the sorted columns from URL/localStorage state.
      initialSort: this.initialSorters(),
      // Let columns use their data widths but stretch to fill the available table width.
      layout: "fitDataStretch",
      // Enable Tabulator pagination controls.
      pagination: true,
      // Show row counts in the pagination footer.
      paginationCounter: "rows",
      // Restore the current page from URL/localStorage state.
      paginationInitialPage: this.tableState.page,
      // Tell Tabulator that pagination happens on the server.
      paginationMode: "remote",
      // Restore the page size from URL/localStorage state.
      paginationSize: this.tableState.size,
      // Let users choose one of the supported page sizes.
      paginationSizeSelector: [10, 25, 50, 100],
      // Empty-state text shown when no rows match the current controls.
      placeholder: "No gauge readings found.",
      // Tell Tabulator that sorting happens on the server.
      sortMode: "remote",
    });

    // Attach lifecycle events after construction so sort/filter redraws preserve scroll.
    this.registerScrollTracking();
    // Attach Tabulator events after scroll tracking exists so snapshots are not sampled too late.
    this.registerScrollPreservationEvents();
  }

  // Initializes the photo-captured-at date/time range picker in the page header.
  // Called by connect() after the Tabulator instance has been created.
  // Returns undefined.
  initializePhotoCapturedAtRangePicker() {
    // The target is optional so this generic table controller can still mount without it.
    if (!this.hasPhotoCapturedAtRangeTarget) return;

    // Restore the input label from URL/localStorage state when a range filter exists.
    const restoredRange = this.restorePhotoCapturedAtRangeInput();

    // Create the picker with time selection enabled and Bootstrap-compatible buttons.
    this.photoCapturedAtRangePicker = new DateRangePicker(this.photoCapturedAtRangeTarget, {
      autoUpdateInput: false,
      alwaysShowCalendars: true,
      buttonClasses: "btn btn-sm",
      applyButtonClasses: "btn-primary",
      cancelButtonClasses: "btn-outline-secondary",
      drops: "down",
      locale: {
        format: this.photoCapturedAtRangePickerFormat(),
        separator: " - ",
        applyLabel: "Apply",
        cancelLabel: "Clear",
      },
      opens: "left",
      ranges: this.photoCapturedAtRangePickerRanges(),
      ...(restoredRange ? { startDate: restoredRange.start, endDate: restoredRange.end } : {}),
      timePicker: true,
      timePickerIncrement: 1,
      timePickerSeconds: true,
    });

    // The library emits apply/cancel from buttons inside its generated popup container.
    this.photoCapturedAtRangeApplyHandler = (event) => this.applyPhotoCapturedAtRange(event);
    this.photoCapturedAtRangeCancelHandler = () => this.clearPhotoCapturedAtRange();
    this.photoCapturedAtRangePicker.container.addEventListener("apply.daterangepicker", this.photoCapturedAtRangeApplyHandler);
    this.photoCapturedAtRangePicker.container.addEventListener("cancel.daterangepicker", this.photoCapturedAtRangeCancelHandler);
  }

  // Applies the selected photo-captured-at range as a Tabulator remote filter.
  // Called by apply.daterangepicker from the header input.
  // Returns undefined.
  applyPhotoCapturedAtRange(event) {
    // Read the picker instance from the custom event emitted by the date range library.
    const picker = event.detail;
    // Build the serialized range payload that Rails can interpret in a later step.
    const filterValue = this.photoCapturedAtRangeFilterValue(picker.startDate, picker.endDate);

    // Reflect the chosen range in the visible control.
    this.updatePhotoCapturedAtRangeDisplay(picker.startDate, picker.endDate);
    // Replace the existing programmatic range filter and ask Tabulator to reload remotely.
    this.table.setFilter(this.photoCapturedAtFiltersWith(filterValue));
  }

  // Clears the selected photo-captured-at range filter.
  // Called by cancel.daterangepicker from the header input.
  // Returns undefined.
  clearPhotoCapturedAtRange() {
    // Clear the visible input first so the header state responds immediately.
    this.clearPhotoCapturedAtRangeInput();
    // Remove the programmatic range filter while leaving normal header filters intact.
    this.table.setFilter(this.photoCapturedAtFiltersWithoutRange());
  }

  // Tears down the photo-captured-at range picker and its controller-owned listeners.
  // Called by disconnect().
  // Returns undefined.
  removePhotoCapturedAtRangePicker() {
    if (!this.photoCapturedAtRangePicker) return;

    if (this.photoCapturedAtRangeApplyHandler) {
      this.photoCapturedAtRangePicker.container.removeEventListener("apply.daterangepicker", this.photoCapturedAtRangeApplyHandler);
    }

    if (this.photoCapturedAtRangeCancelHandler) {
      this.photoCapturedAtRangePicker.container.removeEventListener("cancel.daterangepicker", this.photoCapturedAtRangeCancelHandler);
    }

    this.photoCapturedAtRangePicker.remove();
    this.photoCapturedAtRangePicker = null;
    this.photoCapturedAtRangeApplyHandler = null;
    this.photoCapturedAtRangeCancelHandler = null;
  }

  // Downloads all rows matching the current filters and sorting as an XLSX workbook.
  // Called by the Export XLSX button through data-action="tabulator#exportXlsx".
  // Returns a Promise that resolves after the browser download is triggered.
  async exportXlsx(event) {
    // Keep a reference to the clicked button so it can show an in-progress state.
    const button = event.currentTarget;

    try {
      // Run the export while the button shows a disabled spinner state.
      await this.withButtonSpinner(button, async () => {
        // Fetch every server-side page for the currently filtered/sorted table.
        const rows = await this.fetchGaugeReadingExportRows();
        // Convert fetched JSON rows into an XLSX workbook and trigger the download.
        this.downloadRowsAsXlsx(rows);
      });
    } catch (error) {
      // Log the technical error for debugging failed exports.
      console.error("Gauge readings export failed", error);
      // Give the user a direct failure message because downloads have no inline error surface.
      window.alert("Gauge readings could not be exported. Please try again.");
    }
  }

  // Fetches all JSON rows that match the current table controls.
  // Called by exportXlsx() before building the workbook.
  // Returns a Promise resolving to an array like [{ test_id: "AB12", result_display: "PASS" }].
  async fetchGaugeReadingExportRows() {
    // Ask for the maximum page size currently accepted by GaugeReadingsController.
    const pageSize = 1000;
    // Read current filters/sorters directly from Tabulator so export matches the visible table controls.
    const exportState = this.currentExportState(pageSize);
    // Fetch page 1 first so the response can tell us how many total pages exist.
    const firstPage = await this.fetchGaugeReadingPage(exportState);
    // Copy first-page rows into the final export array.
    const rows = [...firstPage.data];

    // Fetch any remaining pages one at a time to avoid a burst of simultaneous requests.
    for (let page = 2; page <= firstPage.last_page; page += 1) {
      // Fetch this page using the same filters, sorters, and page size.
      const nextPage = await this.fetchGaugeReadingPage({ ...exportState, page });
      // Append this page's rows to the final export array.
      rows.push(...nextPage.data);
    }

    // Return all matching rows to the XLSX builder.
    return rows;
  }

  // Builds export request params from the current Tabulator controls.
  // Called by fetchGaugeReadingExportRows() before requesting page data.
  // Returns an object like { page: 1, size: 1000, filter: [], sort: [] }.
  currentExportState(pageSize) {
    // Start exports from the first page so every matching row can be collected.
    const page = 1;
    // Reuse the requested export page size for every export request.
    const size = pageSize;
    // Read active header and programmatic filters from Tabulator.
    const filter = this.table.getFilters(true);
    // Read active sorters from Tabulator.
    const sort = this.table.getSorters();

    // Return params in the same shape Tabulator sends to ajaxURLGenerator.
    return { page, size, filter, sort };
  }

  // Fetches one page of gauge reading JSON using the existing Rails table endpoint.
  // Called by fetchGaugeReadingExportRows() for page 1 and each remaining page.
  // Returns a Promise resolving to { data: [...], last_page: 3, total_count: 2500 }.
  async fetchGaugeReadingPage(params) {
    // Build the same Rails-compatible query string used for Tabulator AJAX requests.
    const queryString = this.ajaxQueryString(params);
    // Fetch one JSON page from the existing gauge readings endpoint.
    const response = await window.fetch(`${this.ajaxUrlValue}?${queryString}`, {
      // Ask Rails for JSON explicitly.
      headers: { Accept: "application/json" },
      // Include cookies/session data for authenticated requests.
      credentials: "same-origin",
    });

    // Treat non-2xx responses as export failures.
    if (!response.ok) throw new Error(`Export request failed with status ${response.status}`);

    // Parse and return the JSON payload from GaugeReadingsController.
    return response.json();
  }

  // Converts JSON rows into an XLSX workbook and triggers a browser download.
  // Called by exportXlsx() after all rows have been fetched.
  // Returns undefined after SheetJS starts the file download.
  downloadRowsAsXlsx(rows) {
    // Export the same visible columns the user sees in the Tabulator table.
    const columns = this.exportColumns();
    // Create the header row from user-facing column titles.
    const headerRow = columns.map((column) => column.title);
    // Create one worksheet row per JSON row, preserving the visible column order.
    const dataRows = rows.map((row) => columns.map((column) => row[column.field] ?? ""));
    // Convert the array-of-arrays data into a SheetJS worksheet.
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    // Create a new workbook container.
    const workbook = XLSX.utils.book_new();
    // Add the worksheet using a readable Excel tab name.
    XLSX.utils.book_append_sheet(workbook, worksheet, "Gauge Readings");
    // Use today's date in the filename so saved exports are easy to identify.
    const dateStamp = new Date().toISOString().slice(0, 10);
    // Build a stable filename for the downloaded workbook.
    const filename = `gauge-readings-${dateStamp}.xlsx`;

    // Trigger the browser download.
    XLSX.writeFile(workbook, filename);
  }

  // Selects the columns that should appear in the XLSX export.
  // Called by downloadRowsAsXlsx() before worksheet rows are built.
  // Returns an array like [{ field: "test_id", title: "Test ID" }].
  exportColumns() {
    // Export only columns that are visible in the table by default.
    return this.columnsValue.filter((column) => column.visible !== false);
  }

  // Clears filters, sorting, pagination state, localStorage, and table-state URL params.
  // Called by the reset button through data-action="tabulator#resetTableState".
  // Returns a Promise that resolves after Tabulator reloads with default controls.
  async resetTableState(event) {
    // Keep a reference to the clicked button so it can show an in-progress state.
    const button = event.currentTarget;

    // Run the reset while the button shows a disabled spinner state.
    await this.withButtonSpinner(button, async () => {
      // Suppress URL/localStorage writes while Tabulator emits reset-related AJAX requests.
      this.skipTableStatePersistence = true;

      try {
        // Remove the saved browser-local table state.
        this.clearStoredTableState();
        // Remove filters/sorters/page/size from the browser address bar.
        this.clearAddressBarTableState();
        // Reset the in-memory state object to the default table controls.
        this.tableState = this.defaultTableState();
        // Clear the external date range input that lives outside Tabulator's header filters.
        this.clearPhotoCapturedAtRangeInput();
        // Clear Tabulator controls without triggering several overlapping remote reloads.
        this.resetTableControlsWithoutReload();
        // Return to the first page after clearing state.
        const pageReset = this.table.setPage(this.tableState.page);
        // Wait for Tabulator's page reset request to settle before re-enabling persistence.
        await Promise.resolve(pageReset);
      } finally {
        // Future filter/sort/page changes should persist normally again.
        this.skipTableStatePersistence = false;
        // Ensure reset leaves localStorage empty even if Tabulator emitted intermediate requests.
        this.clearStoredTableState();
        // Ensure reset leaves the address bar without table-state params.
        this.clearAddressBarTableState();
      }
    });
  }

  // Clears Tabulator filters, sorting, and page size without immediately loading data.
  // Called by resetTableState() before the one final setPage() reload.
  // Returns undefined.
  resetTableControlsWithoutReload() {
    // Clear programmatic filters such as the external photo-captured-at range picker.
    this.table.modules.filter.clearFilter();
    // Clear visible header filter input values without calling refreshFilter().
    this.table.modules.filter.clearHeaderFilter();
    // Clear active sorters without calling refreshSort().
    this.table.modules.sort.clear();
    // Restore the default page size without calling userSetPageSize(), which also reloads page 1.
    this.table.modules.page.setPageSize(this.tableState.size);
  }

  // Runs an async button action while showing a disabled spinner for at least 300ms.
  // Called by exportXlsx() and resetTableState().
  // Returns the value returned by the callback Promise.
  async withButtonSpinner(button, callback) {
    // Remember the original button HTML so the icon can be restored.
    const originalButtonHtml = button.innerHTML;

    try {
      // Prevent duplicate actions while the callback runs.
      button.disabled = true;
      // Show a compact spinner without expanding the icon-only button.
      button.innerHTML = '<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>';
      // Keep the spinner visible long enough for users to perceive the state change.
      await this.sleep(300);
      // Run the caller's real work after the spinner has painted.
      return await callback();
    } finally {
      // Re-enable the button whether the callback succeeded or failed.
      button.disabled = false;
      // Restore the original button contents.
      button.innerHTML = originalButtonHtml;
    }
  }

  // Pauses execution for a requested number of milliseconds.
  // Called by withButtonSpinner() to keep short actions visibly in progress.
  // Returns a Promise that resolves after ms milliseconds.
  sleep(ms) {
    // Use a Promise wrapper around setTimeout so callers can await the delay.
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Converts Rails-provided column metadata into Tabulator column definitions.
  // Called by initializeTabulator() when the Tabulator instance is created.
  // Returns an array like [{ field: "test_id", title: "Test ID", titleFormatter: Function, sorter: "string" }].
  tableColumns() {
    // Build one Tabulator-safe column definition for each column sent from Rails.
    return this.columnsValue.map((column) => {
      // Clone the metadata so deleting icon does not mutate this.columnsValue.
      const tabulatorColumn = { ...column };
      // Remove the custom icon key because Tabulator does not support it as a column option.
      delete tabulatorColumn.icon;

      // Return the sanitized column definition plus a custom header formatter.
      return {
        // Preserve supported Tabulator options such as field, title, width, sorter, and headerFilter.
        ...tabulatorColumn,
        // Render the header with the original metadata so the icon is still available.
        titleFormatter: () => this.headerTitle(column),
      };
    });
  }

  // Returns header filters that should be restored directly into Tabulator column inputs.
  // Called by initializeTabulator() while building table options.
  // Returns an array of normal header filter objects.
  initialHeaderFilters() {
    // Programmatic filters do not have matching header-filter inputs, so exclude them here.
    return this.tableState.filters.filter((filter) => !this.photoCapturedAtRangeFilter(filter));
  }

  // Returns external/programmatic filters that should be restored outside header inputs.
  // Called by initializeTabulator() while building table options.
  // Returns an array of Tabulator filter objects.
  initialProgrammaticFilters() {
    // Today this is only the photo-captured-at range picker.
    return this.tableState.filters.filter((filter) => this.photoCapturedAtRangeFilter(filter));
  }

  // Converts saved sorter state into the shape Tabulator expects for initialSort.
  // Called by initializeTabulator() when persisted sort state is restored.
  // Returns an array like [{ column: "test_id", dir: "asc" }].
  initialSorters() {
    // Tabulator's remote AJAX params use field, but initialSort expects column.
    return this.tableState.sorters.map((sorter) => ({
      // Pass the saved field as Tabulator's initialSort column name.
      column: sorter.field,
      // Preserve the saved sort direction.
      dir: sorter.dir,
    }));
  }

  // Builds the element used for an icon-enhanced Tabulator column header.
  // Called by the titleFormatter assigned in tableColumns().
  // Returns an HTMLElement containing an icon element and title span.
  headerTitle(column) {
    // Create the wrapper expected by the table header CSS.
    const header = document.createElement("div");
    // Assign the wrapper class that controls icon/text layout.
    header.className = "tabulator-icon-header";

    // Create the Font Awesome icon element.
    const icon = document.createElement("i");
    // Use the Rails-provided icon class, or no class if metadata is missing.
    icon.className = column.icon || "";
    // Hide the decorative icon from assistive technology.
    icon.setAttribute("aria-hidden", "true");

    // Create the visible title text element.
    const title = document.createElement("span");
    // Assign the class that lets long header titles wrap.
    title.className = "tabulator-icon-header-title";
    // Use the Rails-provided title, or empty text if metadata is missing.
    title.textContent = column.title || "";

    // Attach icon and text to the wrapper in display order.
    header.append(icon, title);

    // Give Tabulator the completed header node.
    return header;
  }

  // Chooses the table state source, preferring URL params over localStorage over defaults.
  // Called by connect() before Tabulator is initialized.
  // Returns an object like { filters: [], sorters: [], page: 1, size: 25 }.
  initialTableState() {
    // Parse bookmark/share URL state first so explicit URLs override localStorage.
    const urlState = this.tableStateFromUrl();
    // Use URL state immediately when any table-state query parameter is present.
    if (urlState) return urlState;

    // Fall back to the last saved browser-local table state.
    const storedState = this.tableStateFromStorage();
    // If stored state exists, reflect it in the address bar and use it.
    if (storedState) {
      // Keep plain /gauge_readings visits bookmarkable after localStorage restores state.
      this.updateAddressBar(storedState);
      // Return the saved state to initialize Tabulator.
      return storedState;
    }

    // No URL or localStorage state exists, so start from the standard defaults.
    return this.defaultTableState();
  }

  // Provides the baseline Tabulator state when nothing is saved or bookmarked.
  // Called by initialTableState() when URL and localStorage state are absent.
  // Returns { filters: [], sorters: [], page: 1, size: 25 }.
  defaultTableState() {
    // No header filters should be applied by default.
    const filters = [];
    // No explicit sorters should be applied by default, letting Rails use its fallback order.
    const sorters = [];
    // Start on the first page by default.
    const page = 1;
    // Match the previous hard-coded Tabulator page size.
    const size = 25;

    // Return a complete state object in the same shape used by URL and localStorage state.
    return { filters, sorters, page, size };
  }

  // Parses table state from the current URL query string.
  // Called by initialTableState() before checking localStorage.
  // Returns a table state object, or null when the URL has no table-state params.
  tableStateFromUrl() {
    // Read the current query string without causing navigation.
    const searchParams = new URLSearchParams(window.location.search);
    // Treat URLs without table-state params as absent so localStorage can be used.
    if (!this.hasTableStateParams(searchParams)) return null;

    // Build normalized state from JSON filters/sorters and primitive page/size values.
    return {
      // Decode and sanitize the bookmarkable header filter array.
      filters: this.normalizeFilters(this.parseJsonArrayParam(searchParams.get("filters"))),
      // Decode and sanitize the bookmarkable sorter array.
      sorters: this.normalizeSorters(this.parseJsonArrayParam(searchParams.get("sorters"))),
      // Read a positive page number, falling back to page 1.
      page: this.positiveInteger(searchParams.get("page"), 1),
      // Read a positive page size, falling back to 25.
      size: this.positiveInteger(searchParams.get("size"), 25),
    };
  }

  // Reads the last saved table state from localStorage.
  // Called by initialTableState() when the URL does not contain table-state params.
  // Returns a table state object, or null when storage is empty or unreadable.
  tableStateFromStorage() {
    try {
      // Fetch the serialized state for this table.
      const storedValue = window.localStorage.getItem(this.constructor.storageKey);
      // Empty storage means there is no saved table state to restore.
      if (!storedValue) return null;

      // Convert the serialized JSON string back into an object.
      const parsedValue = JSON.parse(storedValue);

      // Normalize each field so malformed storage cannot break table startup.
      return {
        // Keep only filters that point at real Tabulator columns.
        filters: this.normalizeFilters(parsedValue.filters),
        // Keep only sorters that point at real Tabulator columns.
        sorters: this.normalizeSorters(parsedValue.sorters),
        // Keep a positive saved page, otherwise start on page 1.
        page: this.positiveInteger(parsedValue.page, 1),
        // Keep a positive saved page size, otherwise use 25.
        size: this.positiveInteger(parsedValue.size, 25),
      };
    } catch {
      // Invalid JSON or blocked localStorage should behave like no saved state exists.
      return null;
    }
  }

  // Checks whether a URL contains any table-state query parameters.
  // Called by tableStateFromUrl() to decide whether localStorage should be ignored.
  // Returns true for URLs like /gauge_readings?filters=[] or /gauge_readings?page=2.
  hasTableStateParams(searchParams) {
    // These are the only query params owned by the table-state feature.
    const tableStateParamNames = ["filters", "sorters", "page", "size"];
    // The URL counts as stateful if any one table-state param is present.
    return tableStateParamNames.some((paramName) => searchParams.has(paramName));
  }

  // Parses a JSON query parameter that should contain an array.
  // Called by tableStateFromUrl() for filters and sorters.
  // Returns an array, or [] when the value is blank, invalid, or not an array.
  parseJsonArrayParam(value) {
    // Missing params should act like an empty collection.
    if (!value) return [];

    try {
      // URLSearchParams returns a decoded string, so JSON.parse can read it directly.
      const parsedValue = JSON.parse(value);
      // Accept only arrays because Tabulator expects filter/sorter collections.
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch {
      // Invalid JSON should not prevent the table from loading.
      return [];
    }
  }

  // Parses a value into a positive integer with a fallback.
  // Called whenever page or size values are read from URL, localStorage, or Tabulator params.
  // Returns a positive integer, such as 25, or the provided fallback.
  positiveInteger(value, fallback) {
    // Parse strings like "25" and numbers like 25 the same way.
    const parsedValue = Number.parseInt(value, 10);
    // Reject NaN, zero, and negative values because Tabulator pagination needs positive numbers.
    return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
  }

  // Normalizes persisted header filters into a safe Tabulator filter array.
  // Called by tableStateFromUrl(), tableStateFromStorage(), and normalizeTableState().
  // Returns an array like [{ field: "test_id", type: "like", value: "AB12" }].
  normalizeFilters(filters) {
    // Ignore anything that is not an array because Tabulator expects filter collections.
    if (!Array.isArray(filters)) return [];

    // Keep only filters with a real column field and a non-blank value.
    return filters
      .filter((filter) => this.validTableField(filter?.field) && filter.value !== undefined && filter.value !== null && filter.value !== "")
      .map((filter) => ({
        // Preserve the valid field name.
        field: filter.field,
        // Preserve Tabulator's filter type when present.
        type: filter.type,
        // Preserve the user's filter value.
        value: filter.value,
      }));
  }

  // Normalizes persisted sorters into the remote-AJAX sorter shape.
  // Called by tableStateFromUrl(), tableStateFromStorage(), and normalizeTableState().
  // Returns an array like [{ field: "test_id", dir: "asc" }].
  normalizeSorters(sorters) {
    // Ignore anything that is not an array because Tabulator expects sorter collections.
    if (!Array.isArray(sorters)) return [];

    // Keep only sorters with a real field and a supported direction.
    return sorters
      .map((sorter) => ({
        // Accept field from AJAX state and column from older/initialSort-shaped state.
        field: sorter?.field || sorter?.column,
        // Preserve only the two Tabulator direction values.
        dir: ["asc", "desc"].includes(sorter?.dir) ? sorter.dir : null,
      }))
      .filter((sorter) => this.validTableField(sorter.field) && sorter.dir);
  }

  // Checks whether a field name exists in the Rails-provided Tabulator columns.
  // Called by normalizeFilters() and normalizeSorters() before restoring saved state.
  // Returns true for fields like "test_id" when the table has that column.
  validTableField(field) {
    // Reject blank and non-string fields before checking the column list.
    if (typeof field !== "string" || field.length === 0) return false;

    // Accept only fields that exist in the current table metadata.
    return this.columnsValue.some((column) => column.field === field);
  }

  // Returns the Tabulator field filtered by the external photo-captured-at range picker.
  // Called by picker helpers and table-state splitters.
  // Returns a string field name.
  photoCapturedAtRangeFilterField() {
    return "photo_captured_at_humanized";
  }

  // Returns the date/time format shared by the picker display and serialized filter value.
  // Called by picker helpers.
  // Returns a Moment-compatible format string.
  photoCapturedAtRangePickerFormat() {
    return "MM/DD/YYYY h:mm A";
  }

  // Returns preset ranges shown in the date range picker sidebar.
  // Called when initializing the photo-captured-at picker.
  // Returns an object like { Today: [moment, moment] }.
  photoCapturedAtRangePickerRanges() {
    return {
      Today: [moment().startOf("day"), moment().endOf("day")],
      Yesterday: [moment().subtract(1, "days").startOf("day"), moment().subtract(1, "days").endOf("day")],
      "Last 7 Days": [moment().subtract(6, "days").startOf("day"), moment().endOf("day")],
      "Last 30 Days": [moment().subtract(29, "days").startOf("day"), moment().endOf("day")],
      "This Month": [moment().startOf("month").startOf("day"), moment().endOf("month").endOf("day")],
      "Last Month": [
        moment().subtract(1, "month").startOf("month").startOf("day"),
        moment().subtract(1, "month").endOf("month").endOf("day"),
      ],
    };
  }

  // Checks whether a filter belongs to the external photo-captured-at range picker.
  // Called by table-state splitters and picker helpers.
  // Returns true when the filter is the encoded range filter.
  photoCapturedAtRangeFilter(filter) {
    return filter?.field === this.photoCapturedAtRangeFilterField() && Boolean(this.photoCapturedAtRangeValue(filter.value));
  }

  // Parses a serialized photo-captured-at range filter value.
  // Called when restoring and identifying range filters.
  // Returns { start: String, end: String }, or null for invalid values.
  photoCapturedAtRangeValue(value) {
    if (typeof value !== "string" || value.length === 0) return null;

    try {
      const parsedValue = JSON.parse(value);
      if (typeof parsedValue?.start !== "string" || typeof parsedValue?.end !== "string") return null;

      return { start: parsedValue.start, end: parsedValue.end };
    } catch {
      return null;
    }
  }

  // Serializes a selected date range into one Tabulator filter value.
  // Called when the picker Apply button is clicked.
  // Returns a JSON string.
  photoCapturedAtRangeFilterValue(startDate, endDate) {
    return JSON.stringify({
      start: moment(startDate).format(this.photoCapturedAtRangePickerFormat()),
      end: moment(endDate).format(this.photoCapturedAtRangePickerFormat()),
    });
  }

  // Formats the selected date range for the visible input.
  // Called when applying or restoring picker state.
  // Returns a readable string like "06/06/2026 8:00 AM - 06/06/2026 5:00 PM".
  photoCapturedAtRangeLabel(startDate, endDate) {
    const format = this.photoCapturedAtRangePickerFormat();

    return `${moment(startDate).format(format)} - ${moment(endDate).format(format)}`;
  }

  // Renders a selected date range into the styled display button.
  // Called when applying or restoring picker state.
  // Returns undefined.
  updatePhotoCapturedAtRangeDisplay(startDate, endDate) {
    if (!this.hasPhotoCapturedAtRangeTarget) return;

    const startMoment = moment(startDate, this.photoCapturedAtRangePickerFormat());
    const endMoment = moment(endDate, this.photoCapturedAtRangePickerFormat());

    this.photoCapturedAtRangeTarget.value = this.photoCapturedAtRangeLabel(startMoment, endMoment);
    this.photoCapturedAtRangeTarget.setAttribute("aria-label", `Photo captured date range: ${this.photoCapturedAtRangeTarget.value}`);
    this.photoCapturedAtRangeTarget.classList.add("has-range");
    this.renderPhotoCapturedAtDate("Start", startMoment);
    this.renderPhotoCapturedAtDate("End", endMoment);
  }

  // Renders one side of the styled date range display.
  // Called by updatePhotoCapturedAtRangeDisplay().
  // Returns undefined.
  renderPhotoCapturedAtDate(side, date) {
    this[`photoCapturedAt${side}DayTarget`].textContent = date.format("D");
    this[`photoCapturedAt${side}MonthTarget`].textContent = date.format("MMM");
    this[`photoCapturedAt${side}YearTarget`].textContent = date.format("YYYY");
    this[`photoCapturedAt${side}TimeTarget`].textContent = date.format("h:mm A");
  }

  // Restores the styled date range display to its empty prompt.
  // Called by reset and picker cancel.
  // Returns undefined.
  resetPhotoCapturedAtRangeDisplay() {
    if (!this.hasPhotoCapturedAtRangeTarget) return;

    this.photoCapturedAtRangeTarget.classList.remove("has-range");
    this.photoCapturedAtRangeTarget.setAttribute("aria-label", "Select photo captured date range");
    this.photoCapturedAtStartDayTarget.textContent = "--";
    this.photoCapturedAtStartMonthTarget.textContent = "Select";
    this.photoCapturedAtStartYearTarget.textContent = "Dates";
    this.photoCapturedAtStartTimeTarget.textContent = "Start time";
    this.photoCapturedAtEndDayTarget.textContent = "--";
    this.photoCapturedAtEndMonthTarget.textContent = "Select";
    this.photoCapturedAtEndYearTarget.textContent = "Dates";
    this.photoCapturedAtEndTimeTarget.textContent = "End time";
  }

  // Restores the visible range input from persisted table state.
  // Called by initializePhotoCapturedAtRangePicker().
  // Returns the restored range value, or null when no range exists.
  restorePhotoCapturedAtRangeInput() {
    const filter = this.tableState.filters.find((candidate) => this.photoCapturedAtRangeFilter(candidate));
    const value = this.photoCapturedAtRangeValue(filter?.value);
    if (!value) return null;

    this.updatePhotoCapturedAtRangeDisplay(value.start, value.end);

    return value;
  }

  // Clears only the visible date range input.
  // Called by reset and picker cancel.
  // Returns undefined.
  clearPhotoCapturedAtRangeInput() {
    if (!this.hasPhotoCapturedAtRangeTarget) return;

    this.photoCapturedAtRangeTarget.value = "";
    this.resetPhotoCapturedAtRangeDisplay();
  }

  // Returns programmatic filters with the photo-captured-at range replaced by a new value.
  // Called when applying the picker.
  // Returns an array suitable for table.setFilter().
  photoCapturedAtFiltersWith(filterValue) {
    return [
      ...this.photoCapturedAtFiltersWithoutRange(),
      {
        field: this.photoCapturedAtRangeFilterField(),
        type: "like",
        value: filterValue,
      },
    ];
  }

  // Returns current programmatic filters without the photo-captured-at range filter.
  // Called when clearing or replacing the picker filter.
  // Returns an array suitable for table.setFilter().
  photoCapturedAtFiltersWithoutRange() {
    return this.table.getFilters(false).filter((filter) => !this.photoCapturedAtRangeFilter(filter));
  }

  // Saves the latest Tabulator AJAX params to localStorage and the address bar.
  // Called by ajaxURLGenerator before every remote table request.
  // Returns undefined.
  persistTableState(params) {
    // During explicit reset, let Tabulator request data without saving default state back.
    if (this.skipTableStatePersistence) return;

    // Convert Tabulator's raw AJAX params to the state shape used by URL and localStorage.
    const tableState = this.normalizeTableState(params);

    try {
      // Persist state so plain /gauge_readings visits can restore the same table controls.
      window.localStorage.setItem(this.constructor.storageKey, JSON.stringify(tableState));
    } catch {
      // Ignore storage failures from private browsing or disabled localStorage.
    }

    // Reflect the current state in the browser address bar without a reload.
    this.updateAddressBar(tableState);
  }

  // Removes this table's persisted state from localStorage.
  // Called by resetTableState() before Tabulator controls are cleared.
  // Returns undefined.
  clearStoredTableState() {
    try {
      // Delete only this table's state key so unrelated localStorage values stay intact.
      window.localStorage.removeItem(this.constructor.storageKey);
    } catch {
      // Ignore storage failures from private browsing or disabled localStorage.
    }
  }

  // Normalizes Tabulator AJAX params into the persisted table-state shape.
  // Called by persistTableState() before writing URL/localStorage state.
  // Returns an object like { filters: [{ field: "test_id", value: "AB12" }], sorters: [], page: 1, size: 25 }.
  normalizeTableState(params) {
    // Use only valid Tabulator filters, otherwise store no filters.
    const filters = this.normalizeFilters(params.filter);
    // Use only valid Tabulator sorters, otherwise store no sorters.
    const sorters = this.normalizeSorters(params.sort);
    // Normalize the current remote pagination page.
    const page = this.positiveInteger(params.page, 1);
    // Normalize the current remote pagination page size.
    const size = this.positiveInteger(params.size, 25);

    // Return the canonical state shape shared by localStorage and URL params.
    return { filters, sorters, page, size };
  }

  // Updates the current URL to match table state without reloading the page.
  // Called by persistTableState(), and by initialTableState() when localStorage restores plain /gauge_readings.
  // Returns undefined.
  updateAddressBar(tableState) {
    // Clone the current URL so unrelated query params can be preserved.
    const url = new URL(window.location.href);
    // Work with URLSearchParams because it handles encoding JSON safely.
    const searchParams = new URLSearchParams(url.search);

    // Store filters only when at least one filter is active.
    if (tableState.filters.length > 0) {
      // JSON keeps complex Tabulator filter objects bookmarkable in one query param.
      searchParams.set("filters", JSON.stringify(tableState.filters));
    } else {
      // Remove stale filter params when filters are cleared.
      searchParams.delete("filters");
    }

    // Store sorters only when at least one sorter is active.
    if (tableState.sorters.length > 0) {
      // JSON keeps multi-column Tabulator sorting bookmarkable in one query param.
      searchParams.set("sorters", JSON.stringify(tableState.sorters));
    } else {
      // Remove stale sorter params when sorting is cleared.
      searchParams.delete("sorters");
    }

    // Always store page so copied URLs restore the same pagination position.
    searchParams.set("page", tableState.page.toString());
    // Always store page size so copied URLs restore the same row count.
    searchParams.set("size", tableState.size.toString());

    // Apply the edited query string to the cloned URL.
    url.search = searchParams.toString();
    // Replace the current history entry so filter typing does not spam browser history.
    window.history.replaceState({}, "", url);
  }

  // Removes table-state query params from the address bar without reloading.
  // Called by resetTableState() when the user explicitly clears table controls.
  // Returns undefined.
  clearAddressBarTableState() {
    // Clone the current URL so unrelated query params can be preserved.
    const url = new URL(window.location.href);
    // Work with URLSearchParams so deletes are encoding-safe.
    const searchParams = new URLSearchParams(url.search);

    // Remove persisted filter JSON from the URL.
    searchParams.delete("filters");
    // Remove persisted sorter JSON from the URL.
    searchParams.delete("sorters");
    // Remove persisted page number from the URL.
    searchParams.delete("page");
    // Remove persisted page size from the URL.
    searchParams.delete("size");

    // Apply the edited query string to the cloned URL.
    url.search = searchParams.toString();
    // Replace the current history entry without navigating.
    window.history.replaceState({}, "", url);
  }

  // Registers Tabulator events used to preserve scroll through remote redraws.
  // Called by initializeTabulator() after the Tabulator instance has been created.
  // Returns undefined.
  registerScrollPreservationEvents() {
    // Save before header filter changes trigger a remote data reload.
    this.table.on("dataFiltering", () => this.saveScrollPosition());
    // Save before sorter changes trigger a remote data reload.
    this.table.on("dataSorting", () => this.saveScrollPosition());
    // Save before any remote request not covered by sort/filter events.
    this.table.on("dataLoading", () => this.saveScrollPositionIfMissing());
    // Restore once Tabulator has processed new row data.
    this.table.on("dataProcessed", () => this.restoreScrollPosition());
    // Restore again after final rendering, because Tabulator may reset scroll after dataProcessed.
    this.table.on("renderComplete", () => this.restoreScrollPosition());
    // Re-check Tabulator's tableholder after renders because Tabulator can replace internal elements.
    this.table.on("renderComplete", () => this.ensureTableholderScrollTracker());
  }

  // Starts passive scroll listeners that keep the latest real scroll position cached.
  // Called by initializeTabulator() once Tabulator has created its DOM.
  // Returns undefined.
  registerScrollTracking() {
    // Cache the current scroll values immediately so a redraw has a fallback snapshot.
    this.lastScrollPosition = this.currentScrollPosition();
    // Store the page-level scroll container so removeEventListener can use the same element later.
    this.pageScroller = this.pageScrollerElement();
    // Create one stable listener function for every scroll source.
    this.scrollTracker = () => this.trackScrollPosition();

    // Track normal window scrolling when the document itself owns the scroll.
    window.addEventListener("scroll", this.scrollTracker, { passive: true });

    // Track the app's scroll container when the page is not using window scrolling.
    if (this.pageScroller && this.pageScroller !== document.scrollingElement) {
      // Passive listeners avoid blocking scroll performance.
      this.pageScroller.addEventListener("scroll", this.scrollTracker, { passive: true });
    }

    // Track Tabulator's internal scrollable body separately from the page scroll.
    this.ensureTableholderScrollTracker();
  }

  // Removes passive scroll listeners created by registerScrollTracking().
  // Called by disconnect() before Tabulator is destroyed.
  // Returns undefined.
  unregisterScrollTracking() {
    // There is nothing to remove if tracking was never initialized.
    if (!this.scrollTracker) return;

    // Stop tracking normal window scrolls.
    window.removeEventListener("scroll", this.scrollTracker);

    // Stop tracking the app-level scroll container if one was used.
    if (this.pageScroller && this.pageScroller !== document.scrollingElement) {
      // Use the same listener reference that was passed to addEventListener.
      this.pageScroller.removeEventListener("scroll", this.scrollTracker);
    }

    // Stop tracking the current Tabulator tableholder if one was used.
    if (this.trackedTableholder) {
      // Use the same listener reference that was passed to addEventListener.
      this.trackedTableholder.removeEventListener("scroll", this.scrollTracker);
    }
  }

  // Updates the cached scroll snapshot from the current page and tableholder positions.
  // Called by passive scroll listeners on window, the page scroller, and the Tabulator tableholder.
  // Returns undefined.
  trackScrollPosition() {
    // Ignore Tabulator's redraw-time scroll resets while a saved snapshot is waiting to be restored.
    if (this.savedScrollPosition) return;

    // Store the latest user-visible scroll position for future sort/filter redraws.
    this.lastScrollPosition = this.currentScrollPosition();
  }

  // Keeps the tableholder scroll listener attached to the current Tabulator body element.
  // Called by registerScrollTracking() and Tabulator renderComplete events.
  // Returns undefined.
  ensureTableholderScrollTracker() {
    // Find Tabulator's current scrollable table body.
    const tableholder = this.tableholderElement();
    // Do nothing when Tabulator has not created the body element yet.
    if (!tableholder) return;
    // Do nothing when the listener is already attached to this exact element.
    if (this.trackedTableholder === tableholder) return;

    // Remove the listener from any previous tableholder that Tabulator replaced.
    if (this.trackedTableholder && this.scrollTracker) {
      // Prevent duplicate listeners when Tabulator swaps internal DOM nodes.
      this.trackedTableholder.removeEventListener("scroll", this.scrollTracker);
    }

    // Remember which tableholder currently has the listener.
    this.trackedTableholder = tableholder;
    // Listen for horizontal and vertical scrolling inside the table body.
    tableholder.addEventListener("scroll", this.scrollTracker, { passive: true });
  }

  // Builds a scroll-position snapshot from the page scroller and Tabulator tableholder.
  // Called by registerScrollTracking(), trackScrollPosition(), and saveScrollPosition().
  // Returns an object like { windowY: 0, pageY: 120, tableholderY: 300, tableholderX: 50 }.
  currentScrollPosition() {
    // Resolve the active page-level scroller each time in case the layout changed.
    const pageScroller = this.pageScrollerElement();
    // Locate Tabulator's internal vertically and horizontally scrollable element.
    const tableholder = this.tableholderElement();

    // Return all scroll axes that may need to be restored after Tabulator redraws.
    return {
      // Keep window scroll for layouts where document scrolling is active.
      windowY: window.scrollY,
      // Keep page container vertical scroll for layouts where window.scrollY stays zero.
      pageY: pageScroller?.scrollTop || 0,
      // Keep page container horizontal scroll if a layout ever uses it.
      pageX: pageScroller?.scrollLeft || 0,
      // Preserve the tableholder's vertical scroll position, if it exists.
      tableholderY: tableholder?.scrollTop || 0,
      // Preserve the tableholder's horizontal scroll position, if it exists.
      tableholderX: tableholder?.scrollLeft || 0,
    };
  }

  // Finds the nearest page-level element that actually owns vertical scrolling.
  // Called by registerScrollTracking(), currentScrollPosition(), and applyScrollPosition().
  // Returns an HTMLElement such as .holygrail-body, or document.scrollingElement as fallback.
  pageScrollerElement() {
    // Start from the controller element because it lives inside the scrollable page layout.
    let element = this.element;

    // Walk upward through ancestors until a real scroll container is found.
    while (element && element !== document.body) {
      // Return the first ancestor that can scroll vertically.
      if (this.elementCanScroll(element)) return element;
      // Continue checking the next parent in the layout.
      element = element.parentElement;
    }

    // Fall back to the browser's document scroller for simpler layouts.
    return document.scrollingElement || document.documentElement;
  }

  // Checks whether an element can be the page-level vertical scroll container.
  // Called by pageScrollerElement() while walking ancestors.
  // Returns true when the element has scrollable overflow and overflowing content.
  elementCanScroll(element) {
    // Read computed CSS because scrollability depends on overflow rules, not only dimensions.
    const style = window.getComputedStyle(element);
    // Only auto/scroll/overlay containers should be treated as scroll owners.
    const scrollableOverflow = ["auto", "scroll", "overlay"].includes(style.overflowY);
    // The element must have more content than visible area to currently own scrolling.
    const hasScrollableContent = element.scrollHeight > element.clientHeight;

    // Return true only for a real vertical scroll container.
    return scrollableOverflow && hasScrollableContent;
  }

  // Saves the latest known page and Tabulator tableholder scroll positions.
  // Called by Tabulator dataFiltering, dataSorting, and pageLoaded events before redraws.
  // Returns true so Tabulator continues the AJAX request.
  saveScrollPosition() {
    // Keep the first snapshot for this redraw so duplicate Tabulator events cannot overwrite it with 0.
    if (this.savedScrollPosition) return true;

    // Use the continuously tracked value first because Tabulator events can fire after scroll resets.
    this.savedScrollPosition = this.lastScrollPosition || this.currentScrollPosition();

    // Returning true keeps this usable from callbacks that inspect a boolean result.
    return true;
  }

  // Saves scroll only when no earlier sort/filter snapshot is waiting to be restored.
  // Called by Tabulator's dataLoading event as a fallback for other remote reloads.
  // Returns true so event callers can continue their work.
  saveScrollPositionIfMissing() {
    // Preserve the earlier dataFiltering/dataSorting snapshot when it exists.
    if (this.savedScrollPosition) return true;

    // Otherwise capture the current scroll position as a fallback.
    return this.saveScrollPosition();
  }

  // Restores the browser window and Tabulator tableholder scroll positions after table redraw.
  // Called by Tabulator dataProcessed and renderComplete events after rows change.
  // Returns undefined.
  restoreScrollPosition() {
    // Do nothing when there is no saved scroll position yet.
    if (!this.savedScrollPosition) return;

    // Copy the saved value so each scheduled restore uses the same coordinates.
    const scrollPosition = this.savedScrollPosition;
    // Restore over multiple frames because Tabulator may reset scroll more than once per redraw.
    const frameDelays = [0, 1, 2, 4, 8];

    // Apply the same snapshot repeatedly while Tabulator finishes rendering.
    frameDelays.forEach((frameDelay, index) => {
      // Schedule the restore after the requested number of animation frames.
      this.afterAnimationFrames(frameDelay, () => {
        // Re-apply the saved coordinates after this frame delay.
        this.applyScrollPosition(scrollPosition);
        // The last scheduled restore can release the snapshot and resume live tracking.
        if (index === frameDelays.length - 1) this.finishScrollRestore();
      });
    });
  }

  // Applies one saved scroll snapshot to the page and Tabulator tableholder.
  // Called by restoreScrollPosition() after one or more animation frames.
  // Returns undefined.
  applyScrollPosition(scrollPosition) {
    // Resolve the current page scroll container in case layout changed during the redraw.
    const pageScroller = this.pageScrollerElement();

    // Restore the app-level scroll container used by this layout.
    if (pageScroller && pageScroller !== document.scrollingElement) {
      // Restore horizontal page-container scroll first.
      pageScroller.scrollLeft = scrollPosition.pageX;
      // Restore vertical page-container scroll, which is why windowY can remain 0.
      pageScroller.scrollTop = scrollPosition.pageY;
    }

    // Restore the browser window scroll without smooth animation.
    window.scrollTo({ top: scrollPosition.windowY, left: window.scrollX, behavior: "instant" });

    // Locate the current tableholder after Tabulator has redrawn.
    const tableholder = this.tableholderElement();
    // Do nothing else if the tableholder is unavailable.
    if (!tableholder) return;

    // Restore the tableholder's horizontal scroll position.
    tableholder.scrollLeft = scrollPosition.tableholderX;
    // Restore the tableholder's vertical scroll position.
    tableholder.scrollTop = scrollPosition.tableholderY;
  }

  // Finishes a scheduled scroll restore and resumes normal scroll tracking.
  // Called by the last frame callback scheduled in restoreScrollPosition().
  // Returns undefined.
  finishScrollRestore() {
    // Refresh the cached value so future redraws start from the restored coordinates.
    this.lastScrollPosition = this.currentScrollPosition();
    // Clear the pending snapshot so normal user scroll tracking can resume.
    this.savedScrollPosition = null;
  }

  // Runs a callback after a requested number of animation frames.
  // Called by restoreScrollPosition() to outlast Tabulator's own scroll resets.
  // Returns undefined.
  afterAnimationFrames(frameCount, callback) {
    // Run immediately on the next task when no extra frame delay is requested.
    if (frameCount <= 0) {
      // Schedule with requestAnimationFrame so DOM layout has a chance to settle.
      window.requestAnimationFrame(callback);
      // Stop here because the callback has been scheduled.
      return;
    }

    // Wait one animation frame, then recurse until the requested delay is reached.
    window.requestAnimationFrame(() => this.afterAnimationFrames(frameCount - 1, callback));
  }

  // Finds Tabulator's scrollable tableholder element.
  // Called by saveScrollPosition() and restoreScrollPosition().
  // Returns an HTMLElement like .tabulator-tableholder, or null.
  tableholderElement() {
    // Query inside this controller's table target so other Tabulator instances are ignored.
    return this.tableTarget.querySelector(".tabulator-tableholder");
  }

  // Builds the Rails-compatible query string for Tabulator's remote AJAX request.
  // Called by ajaxURLGenerator before every remote table request.
  // Returns a string like "page=1&size=25&filter[0][field]=test_id&filter[0][value]=AB12".
  ajaxQueryString(params) {
    // Start with an empty query string builder for the outgoing AJAX URL.
    const searchParams = new URLSearchParams();

    // Add the requested page expected by GaugeReadingsController.
    searchParams.set("page", this.positiveInteger(params.page, 1).toString());
    // Add the requested page size expected by GaugeReadingsController.
    searchParams.set("size", this.positiveInteger(params.size, 25).toString());
    // Add Tabulator filters using Rails nested-param names.
    this.appendCollectionParams(searchParams, "filter", params.filter);
    // Add Tabulator sorters using Rails nested-param names.
    this.appendCollectionParams(searchParams, "sort", params.sort);

    // Return the encoded query string for ajaxURLGenerator.
    return searchParams.toString();
  }

  // Appends an array of Tabulator objects as Rails nested query params.
  // Called by ajaxQueryString() for filter and sort arrays.
  // Returns undefined after mutating the provided URLSearchParams.
  appendCollectionParams(searchParams, paramName, collection) {
    // Ignore missing collections because there is nothing to append.
    if (!Array.isArray(collection)) return;

    // Walk each filter/sorter object with its array index.
    collection.forEach((item, index) => {
      // Convert object keys like field/value/type/dir into nested Rails param keys.
      Object.entries(item).forEach(([key, value]) => {
        // Skip blank values so empty header filters do not become active server filters.
        if (value === undefined || value === null || value === "") return;

        // Append a param like filter[0][field]=test_id or sort[0][dir]=asc.
        searchParams.append(`${paramName}[${index}][${key}]`, value);
      });
    });
  }
}
